import { z } from 'zod';
import { BannerPlacement } from '../enums.js';

/** Admin-managed banner / campaign model (spec section 11). */
export const bannerBaseSchema = z.object({
  placement: z.nativeEnum(BannerPlacement),
  title: z.string().max(160).nullable().optional(),
  subtitle: z.string().max(300).nullable().optional(),
  desktopMediaId: z.string().uuid().nullable().optional(),
  mobileMediaId: z.string().uuid().nullable().optional(),
  ctaLabel: z.string().max(40).nullable().optional(),
  ctaUrl: z.string().max(500).nullable().optional(),
  theme: z.enum(['light', 'dark']).default('light'),
  startAt: z.string().datetime().nullable().optional(),
  endAt: z.string().datetime().nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
  audience: z.string().max(60).nullable().optional(),
  altText: z.string().min(1).max(300),
  trackingKey: z.string().max(80).nullable().optional(),
});

export const bannerInputSchema = bannerBaseSchema
  .refine(
    (b) => !b.startAt || !b.endAt || new Date(b.startAt) < new Date(b.endAt),
    { message: 'startAt must be before endAt', path: ['endAt'] },
  )
  .refine(
    (b) => {
      if (!b.ctaUrl) return true;
      // Internal route or validated absolute URL only
      if (b.ctaUrl.startsWith('/')) return true;
      try {
        const u = new URL(b.ctaUrl);
        return u.protocol === 'https:' || u.protocol === 'http:';
      } catch {
        return false;
      }
    },
    { message: 'ctaUrl must be an internal path or a valid http(s) URL', path: ['ctaUrl'] },
  );

export type BannerInput = z.infer<typeof bannerInputSchema>;

export interface BannerImageSize {
  width: number;
  height: number;
}

/**
 * Pixel size banner images are cropped to when uploaded (admin) - 2x the
 * on-screen size so they stay sharp on retina. The storefront frames each
 * placement at the same proportions (see BannerSlider: hero is 7:3 from
 * tablet up and 16:10 on phones; promo tiles are 11:5), so what the admin
 * crops is what visitors see. Change a ratio here and in BannerSlider.
 * Placements not listed here are not rendered on the storefront yet.
 */
export const BANNER_IMAGE_SIZES: Partial<Record<BannerPlacement, { desktop: BannerImageSize; mobile: BannerImageSize }>> = {
  [BannerPlacement.HOME_HERO]: {
    desktop: { width: 2100, height: 900 },
    mobile: { width: 1280, height: 800 },
  },
  [BannerPlacement.HOME_SECONDARY]: {
    desktop: { width: 1100, height: 500 },
    mobile: { width: 1100, height: 500 },
  },
};
